<template class="chat-list">
    <div class="chat-user" v-if="relatedUser" :class="{ 'selected': isSelected }">
        <div class="chat-user__left">
            <span class="user-menu__avatar">
                <img v-if="relatedUser && relatedUser.attributes && relatedUser.attributes.image_url"
                    :src="relatedUser.attributes.image_url" alt="avatar"
                    onerror="this.onerror=null;this.src='/img/icons/user.png'">
                <svg v-else width="18" height="20" viewBox="0 0 18 20">
                    <use xlink:href="#svg-icn-user"></use>
                </svg>
                <div v-if="relatedUserIsOnline" class="is-online">online</div>
            </span>
        </div>
        <div class="chat-user__right">
            <div class="chat-user__header">
                <template>
                    <strong class="mb-0">
                        {{ handleTwilioUserNameOf(relatedUser) }}
                    </strong>
                </template>
                <template v-if="lastMessage">
                    <span class="chat-user__timestamp">{{ lastMessageDate }}</span>
                </template>
            </div>
            <p class="chat-user__subject">
                {{ conversation.friendlyName || conversation.sid }}
                <span class="chat-user__count-unread" v-if="unreadMessagesCount">
                    {{ unreadMessagesCount }}
                </span>
            </p>
            <div class="chat-user__last-message" v-if="lastMessageBody">
                {{ lastMessageBody }}
            </div>
            <a class="me-2" v-if="this.conversationsClient.user.attributes.account_type === 'agent'"
                @click.prevent="showUnsubscribeModal = true">Unsubscribe</a>
            <a class="me-2" v-if="status" @click.prevent="showChangeStateModal = true">Set inactive</a>
            <a class="me-2" v-else @click.prevent="showChangeStateModal = true">Set active</a>
            <a class="me-2"
                v-if="this.conversationsClient.user.attributes.account_type === 'company' && this.filteredUserAgents.length"
                @click.prevent="showMoveAgentChatModal = true">Move</a>
            <UnsubscribeModal :show-modal="showUnsubscribeModal" :unsubscribe-url="unsubscribeUrl"
                @close="showUnsubscribeModal = false" />
            <ChangeStateModal :show-modal="showChangeStateModal" :change-state-url="changeStateUrl"
                @close="showChangeStateModal = false" @changeState="$emit('changeState')" :status="status" />
            <MoveAgentChatModal :show-modal="showMoveAgentChatModal" @close="showMoveAgentChatModal = false"
                :agent="agent" :conversation="conversation" />
        </div>
    </div>
</template>

<script>
import ChangeStateModal from '@components/UserChat/partials/ChangeStateModal.vue';
import MoveAgentChatModal from '@components/UserChat/partials/MoveAgentChatModal.vue';
import UnsubscribeModal from '@components/UserChat/partials/UnsubscribeModal.vue';
import { handleTwilioUserNameOf } from '@helpers/helpers';
import moment from 'moment';
import { mapState } from 'vuex';

export default {
    components: {
        UnsubscribeModal,
        ChangeStateModal,
        MoveAgentChatModal,
    },
    props: {
        conversationsClient: {
            type: Object,
            required: true,
            default: null,
        },
        conversation: {
            type: Object,
            required: true,
        },
        isSelected: {
            type: Boolean,
            required: true,
        },
        status: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            unreadMessagesCount: null,
            lastMessage: null,
            lastMessageDate: null,
            lastMessageBody: null,
            relatedUser: null,
            relatedUserIsOnline: false,
            unsubscribeUrl: null,
            showUnsubscribeModal: false,
            changeStateUrl: null,
            showChangeStateModal: false,
            showMoveAgentChatModal: false,
            agent: null,
            conversationInfo: {
                conversation: null,
                users: [],
            },
        };
    },
    computed: {
        ...mapState('user', ['filteredUserAgents']),
    },
    async mounted() {
        // needs refactoring, move url generation to BE
        this.unsubscribeUrl = `/chat/unsubscribe/${this.conversation.sid}`;
        this.changeStateUrl = `/api/chat/conversations/change-state/${this.conversation.sid}`;

        await this.setLastMessage();
        this.setUnreadMessagesCount();

        this.conversation.onWithReplay('messageAdded', async (message) => {
            this.setLastMessage(message);

            if (this.isSelected) {
                await this.conversation.advanceLastReadMessageIndex(this.lastMessage.index);
            }
            this.setUnreadMessagesCount();
        });

        const chatUserAsParticipant = await this.conversation.getParticipantByIdentity(this.conversationsClient.user.identity);
        const conversationParticipants = await this.conversation.getParticipants();

        this.conversationInfo.conversation = this.conversation;

        this.relatedUser = null;

        conversationParticipants.forEach(async (participant) => {
            const user = await participant.getUser();

            if (user.attributes.account_type === 'agent') {
                this.agent = user;
            }

            if (chatUserAsParticipant.identity === participant.identity) {
                return;
            }
            if (user.attributes.is_deleted) {
                return;
            }

            this.conversationInfo.users.push(handleTwilioUserNameOf(user));

            user.onWithReplay('updated', (event) => {
                this.relatedUserIsOnline = event.user.isOnline;
            });

            if (conversationParticipants.length === 2) {
                this.relatedUser = user;
            } else if (conversationParticipants.length === 3) {
                if (['agent', 'company'].includes(this.conversationsClient.user.attributes.account_type) && user.attributes.account_type === 'individual') {
                    this.relatedUser = user;
                } else if (chatUserAsParticipant.attributes.group !== participant.attributes.group && !this.relatedUser) {
                    this.relatedUser = user;
                }
            }
        });
        this.$emit('setConversationInfo', this.conversationInfo);
    },
    watch: {
        async isSelected(isSelected) {
            if (isSelected) {
                await this.conversation.advanceLastReadMessageIndex(this.conversation.lastMessage.index);
                this.setUnreadMessagesCount();
                this.$router.push({ params: { conversationSid: this.conversation.sid } });
            }
        },
    },
    methods: {
        handleTwilioUserNameOf,
        async setLastMessage(message = null) {
            if (message) {
                this.lastMessage = message;
            } else {
                const m = await this.conversation.getMessages(1, this.conversation.lastMessage.index);
                this.lastMessage = m.items.shift();
            }
            this.lastMessageBody = this.lastMessage.attributes.is_deleted ? 'Deleted message' : this.lastMessage.state.body;
            const date = moment(this.lastMessage.state.timestamp);
            const duration = moment.duration(moment().diff(date));
            const hours = duration.asHours();
            this.lastMessageDate = (hours > 48) ? date.format('DD/MM/YYYY') : date.fromNow();
        },
        async setUnreadMessagesCount() {
            this.unreadMessagesCount = await this.conversation.getUnreadMessagesCount();
            if (this.unreadMessagesCount === null) {
                this.unreadMessagesCount = await this.conversation.getMessagesCount();
            }
        },
    },
};
</script>
